'use client';

/**
 * PropertyListCard Component
 *
 * Horizontal card layout for list view display of properties
 * Core reusable component - theme-agnostic
 *
 * Now uses FormatPrice for currency-aware price display with automatic conversion.
 * Now uses WishlistButton for saving properties to Route Royal wishlists.
 */

import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';
import { WishlistButton } from '@/components/customer/WishlistButton';
import type { Property } from '@/lib/api/clients/properties';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

export interface PropertyListCardProps {
  /**
   * Property data
   */
  property: Property;
  /**
   * Optional labels for translations
   */
  labels?: {
    bedrooms?: string;
    bathrooms?: string;
    area?: string;
    viewDetails?: string;
    contactAgent?: string;
    save?: string;
    saved?: string;
    share?: string;
    askingPrice?: string;
    built?: string;
    renovated?: string;
    parking?: string;
    sold?: string;
    reserved?: string;
    contactForPrice?: string;
  };
  /**
   * Optional location name to display instead of reference
   */
  locationName?: string;
  /**
   * Link href
   */
  href?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Show action buttons
   */
  showActions?: boolean;
  /**
   * Whether to show original price alongside converted price
   */
  showOriginalPrice?: boolean;
  /**
   * Icon components (optional - themes can provide custom icons)
   */
  icons?: {
    bedroom?: GenericIconComponent;
    bathroom?: GenericIconComponent;
    area?: GenericIconComponent;
    save?: GenericIconComponent;
    share?: GenericIconComponent;
    phone?: GenericIconComponent;
    arrow?: GenericIconComponent;
    check?: GenericIconComponent;
  };
  /**
   * Icon colors (optional - themes can provide custom colors)
   */
  iconColors?: {
    bedroom?: string;
    bathroom?: string;
    area?: string;
  };
  /**
   * Whether to show the wishlist (heart) button
   * @default true
   */
  showWishlistButton?: boolean;
  /**
   * Callbacks
   */
  onSave?: (propertyId: string) => void;
  onShare?: (propertyId: string) => void;
  onContact?: (propertyId: string) => void;
}

// Generic icon props - compatible with various icon libraries
// Using Record<string, unknown> to accept any icon prop pattern
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericIconComponent = React.ComponentType<any>;

/**
 * PropertyListCard - Horizontal layout for list views
 */
export function PropertyListCard({
  property,
  labels = {
    bedrooms: 'Beds',
    bathrooms: 'Baths',
    area: 'Area',
    viewDetails: 'View Details',
    contactAgent: 'Contact Agent',
    save: 'Save',
    saved: 'Saved',
    share: 'Share',
    askingPrice: 'Asking Price',
    built: 'Built',
    renovated: 'Renovated',
    parking: 'Parking',
    sold: 'Sold',
    reserved: 'Reserved',
    contactForPrice: 'Contact for price',
  },
  href,
  className = '',
  showActions = true,
  showOriginalPrice = false,
  showWishlistButton = true,
  locationName,
  icons,
  iconColors = {
    bedroom: '#1f2937',
    bathroom: '#2563eb',
    area: '#374151',
  },
  onSave,
  onShare,
  onContact,
}: PropertyListCardProps) {
  const [showSharePulse, setShowSharePulse] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setShowSharePulse(true);
    setTimeout(() => setShowSharePulse(false), 600);

    if (onShare) {
      onShare(property.id);
    }
  };

  const handleContact = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onContact) {
      onContact(property.id);
    }
  };

  // Format property data
  const firstImage =
    property.primaryImageUrl ||
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';

  const { amount: askingPrice, currency } = extractBestPriceSimple(property);
  const hasPrice = askingPrice != null && askingPrice > 0;

  const area = property.totalFloorArea
    ? `${property.totalFloorArea.toLocaleString()} ${(property.totalFloorAreaUnit || 'sqft').toLowerCase()}`
    : null;

  const landArea = property.landArea
    ? `${property.landArea.toLocaleString()} ${(property.landAreaUnit || 'sqft').toLowerCase()}`
    : null;

  // Calculate total parking from all sources
  const totalParking =
    (property.garageCount || 0) +
    (property.carportCount || 0) +
    (property.openParkingCount || 0) +
    (property.parkingSpaces || 0);

  // Note: href should always be provided with locale prefix
  // Fallback is for backwards compatibility only
  const linkHref = href || `/en/properties/${property.id}`;

  const cardContent = (
    <motion.article
      className={`group flex flex-col sm:flex-row gap-0 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-gray-300 overflow-hidden ${className}`}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -4 }}
      style={{
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* Image Section */}
      <div className="relative w-full sm:w-[400px] h-72 sm:h-auto shrink-0 overflow-hidden">
        <Image
          src={firstImage}
          alt={property.name}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-110"
          sizes="(max-width: 640px) 100vw, 400px"
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

        {/* Status badge */}
        {property.status && property.status !== 'ACTIVE' && (
          <div className="absolute top-4 left-4 z-10">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold bg-gray-900/90 backdrop-blur-md text-white shadow-lg border border-white/20">
              {(property.status === 'ARCHIVED' || property.status === 'INACTIVE' || (property as unknown as Record<string, unknown>).listingStatus === 'SOLD')
                ? labels.sold
                : property.status}
            </span>
          </div>
        )}

        {/* Reference Number Badge - Bottom Left */}
        {property.reference && (
          <motion.div
            className="absolute bottom-4 left-4 z-10"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : -20 }}
            transition={{ duration: 0.3 }}
          >
            <span
              className="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold bg-white/95 backdrop-blur-md text-gray-900 shadow-lg border border-white/40"
              style={{
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              }}
            >
              {property.reference}
            </span>
          </motion.div>
        )}

        {/* Wishlist & Share Buttons - Top Right */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          {/* Wishlist Button - saves to Route Royal (independent of showActions) */}
          {showWishlistButton && (
            <div className="w-11 h-11 flex items-center justify-center bg-white/95 backdrop-blur-md rounded-full shadow-lg">
              <WishlistButton
                entityType="PROPERTY"
                entityId={property.id}
                variant="ghost"
                size="md"
                onChange={() => onSave?.(property.id)}
              />
            </div>
          )}

          {/* Share Button with Micro-interactions (only when showActions is true) */}
          {showActions && (
            <motion.button
              className="w-11 h-11 flex items-center justify-center bg-white/95 backdrop-blur-md rounded-full shadow-lg hover:bg-white transition-colors"
              onClick={handleShare}
              aria-label={labels.share}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <motion.svg
                className="w-5 h-5 text-gray-700 hover:text-blue-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
                animate={
                  showSharePulse ? { rotate: [0, -12, 12, -8, 8, 0] } : {}
                }
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </motion.svg>

              {/* Share pulse effect - expanding circles */}
              <AnimatePresence>
                {showSharePulse && (
                  <>
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-500"
                      initial={{ scale: 1, opacity: 0.8 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                    />
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-400"
                      initial={{ scale: 1, opacity: 0.6 }}
                      animate={{ scale: 2.2, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    />
                    {/* Radiating dots effect */}
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 bg-blue-500 rounded-full"
                        initial={{
                          x: 0,
                          y: 0,
                          scale: 1,
                          opacity: 1,
                        }}
                        animate={{
                          x: Math.cos((i * Math.PI * 2) / 6) * 25,
                          y: Math.sin((i * Math.PI * 2) / 6) * 25,
                          scale: 0,
                          opacity: 0,
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        style={{
                          left: '50%',
                          top: '50%',
                          marginLeft: '-3px',
                          marginTop: '-3px',
                        }}
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>
            </motion.button>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 flex flex-col p-8 relative">
        {/* Header */}
        <div className="flex-1 mb-6">
          <div className="flex items-start justify-between gap-6 mb-4">
            <div className="flex-1">
              <h2
                className="text-3xl font-extrabold text-gray-900 group-hover:text-[#243462] transition-colors leading-tight mb-2"
                style={{
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
              >
                {property.name}
              </h2>
              {locationName && (
                <p
                  className="text-sm font-semibold text-gray-500 flex items-center gap-1.5"
                  style={{
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {locationName}
                </p>
              )}
            </div>

            {/* Price - Now uses FormatPrice with currency conversion */}
            <div className="text-right">
              {hasPrice ? (
                <>
                  <motion.div
                    className="text-3xl font-extrabold text-gray-900 whitespace-nowrap"
                    style={{
                      fontFamily:
                        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <FormatPrice
                      amount={askingPrice}
                      currency={currency}
                      size="xl"
                      showOriginal={showOriginalPrice}
                    />
                  </motion.div>
                  <p
                    className="text-xs text-gray-500 font-medium mt-1"
                    style={{
                      fontFamily:
                        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    }}
                  >
                    {labels.askingPrice}
                  </p>
                </>
              ) : (
                <p
                  className="text-base font-medium text-gray-500 italic whitespace-nowrap"
                  style={{
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  {labels.contactForPrice}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <p
            className="text-gray-600 leading-relaxed mb-6 line-clamp-2 text-base"
            style={{
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            {property.description || property.name}
          </p>

          {/* Property Details - Icons */}
          {(property.bedrooms ||
            property.bathrooms ||
            area ||
            landArea ||
            totalParking > 0) && (
            <div className="flex items-center flex-wrap gap-6 mb-6">
              {property.bedrooms && (
                <motion.div
                  className="flex items-center gap-2.5"
                  whileHover={{ scale: 1.05 }}
                >
                  {icons?.bedroom ? (
                    <icons.bedroom
                      size={28}
                      weight="light"
                      className={`text-[${iconColors.bedroom}]`}
                      style={{ color: iconColors.bedroom }}
                    />
                  ) : (
                    <svg
                      className="w-7 h-7"
                      style={{ color: iconColors.bedroom }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a2 2 0 012-2h6a2 2 0 012 2v4" />
                    </svg>
                  )}
                  <div>
                    <p
                      className="text-2xl font-extrabold text-gray-900"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {property.bedrooms}
                    </p>
                    <p
                      className="text-xs text-gray-500 font-medium uppercase tracking-wide"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {labels.bedrooms}
                    </p>
                  </div>
                </motion.div>
              )}

              {property.bathrooms && (
                <motion.div
                  className="flex items-center gap-2.5"
                  whileHover={{ scale: 1.05 }}
                >
                  {icons?.bathroom ? (
                    <icons.bathroom
                      size={28}
                      weight="light"
                      className={`text-[${iconColors.bathroom}]`}
                      style={{ color: iconColors.bathroom }}
                    />
                  ) : (
                    <svg
                      className="w-7 h-7"
                      style={{ color: iconColors.bathroom }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                    >
                      <path d="M17 11v10M3 11h18M3 8a3 3 0 013-3h12a3 3 0 013 3" />
                      <circle cx="7" cy="16" r="1.5" />
                    </svg>
                  )}
                  <div>
                    <p
                      className="text-2xl font-extrabold text-gray-900"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {property.bathrooms}
                    </p>
                    <p
                      className="text-xs text-gray-500 font-medium uppercase tracking-wide"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {labels.bathrooms}
                    </p>
                  </div>
                </motion.div>
              )}

              {area && (
                <motion.div
                  className="flex items-center gap-2.5"
                  whileHover={{ scale: 1.05 }}
                >
                  {icons?.area ? (
                    <icons.area
                      size={28}
                      weight="light"
                      className={`text-[${iconColors.area}]`}
                      style={{ color: iconColors.area }}
                    />
                  ) : (
                    <svg
                      className="w-7 h-7"
                      style={{ color: iconColors.area }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                    >
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                      <path d="M4 4l16 16M20 4L4 20" />
                    </svg>
                  )}
                  <div>
                    <p
                      className="text-xl font-extrabold text-gray-900"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {area}
                    </p>
                    <p
                      className="text-xs text-gray-500 font-medium uppercase tracking-wide"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {labels.area}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Land Area (if no floor area or for land listings) */}
              {landArea && !area && (
                <motion.div
                  className="flex items-center gap-2.5"
                  whileHover={{ scale: 1.05 }}
                >
                  <svg
                    className="w-7 h-7 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
                    />
                  </svg>
                  <div>
                    <p
                      className="text-xl font-extrabold text-gray-900"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {landArea}
                    </p>
                    <p
                      className="text-xs text-gray-500 font-medium uppercase tracking-wide"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      Land
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Parking - combined from all sources */}
              {totalParking > 0 && (
                <motion.div
                  className="flex items-center gap-2.5"
                  whileHover={{ scale: 1.05 }}
                >
                  <svg
                    className="w-7 h-7 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                    />
                  </svg>
                  <div>
                    <p
                      className="text-2xl font-extrabold text-gray-900"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {totalParking}
                    </p>
                    <p
                      className="text-xs text-gray-500 font-medium uppercase tracking-wide"
                      style={{
                        fontFamily:
                          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {labels.parking}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Additional Details - Badges */}
          {(property.yearBuilt ||
            property.yearRenovated ||
            property.garageCount) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {property.yearBuilt && (
                <span
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold"
                  style={{
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  {labels.built} {property.yearBuilt}
                </span>
              )}
              {property.yearRenovated && (
                <span
                  className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold"
                  style={{
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  {labels.renovated} {property.yearRenovated}
                </span>
              )}
              {property.garageCount && property.garageCount > 0 && (
                <span
                  className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-semibold flex items-center gap-1"
                  style={{
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {property.garageCount} Garage
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions Footer */}
        {showActions && (
          <div className="flex items-center gap-3 pt-6 border-t-2 border-gray-100">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContact}
              className="flex-1 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold text-sm rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 group"
              style={{
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              }}
            >
              <span>{labels.viewDetails}</span>
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContact}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
              style={{
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="hidden lg:inline">{labels.contactAgent}</span>
            </motion.button>
          </div>
        )}
      </div>
    </motion.article>
  );

  return linkHref ? (
    <Link href={linkHref} className="block">
      {cardContent}
    </Link>
  ) : (
    cardContent
  );
}
